// Page Me — the one door to the server.
//
// Every call resolves (never rejects) to either the server's JSON with
// `ok: true` added, or `{ ok: false, error, message, httpStatus }`. Network
// failures are `error: 'offline'` and flip `state.online` off; any response at
// all flips it back on. Never logs the token or a body.

import { API_BASE } from '../core/native.js';
import { setState } from '../core/state.js';

export const TIMEOUT_MS = 10_000;

let authToken = null;
/** Set by the store once it has (or loses) a device token. */
export function setAuthToken(token) { authToken = token || null; }
export function hasAuthToken() { return !!authToken; }

// Called with the token that was refused when the server answers 401 to an
// authenticated call — the number was restored on another phone, or deleted.
let onUnauthorized = null;
export function setOnUnauthorized(fn) { onUnauthorized = fn || null; }

// Test seam: swap fetch without touching globals.
let fetchImpl = (...a) => fetch(...a);
export function setFetch(fn) { fetchImpl = fn ?? ((...a) => fetch(...a)); }

let base = API_BASE;
export function setApiBase(b) { base = b ?? API_BASE; }

const OFFLINE_MESSAGE = "Page Me can't reach the network. It'll try again when you're back online.";

function markOnline(on) {
  try { setState({ online: on }); } catch { /* no state in tests */ }
}

/**
 * @param {string} method
 * @param {string} path   '/api/...'
 * @param {object} [body]
 * @param {{ auth?: boolean, timeout?: number }} [opts]
 */
export async function request(method, path, body, { auth = true, timeout = TIMEOUT_MS } = {}) {
  const headers = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const sentToken = auth ? authToken : null;
  if (auth) {
    if (!authToken) return { ok: false, error: 'unauthorized', message: "This pager isn't signed in.", httpStatus: 0 };
    headers.Authorization = `Bearer ${authToken}`;
  }

  const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), timeout) : 0;
  let res;
  try {
    res = await fetchImpl(`${base}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: ctrl?.signal,
      cache: 'no-store',
    });
  } catch (e) {
    clearTimeout(timer);
    markOnline(false);
    const timedOut = e?.name === 'AbortError';
    return { ok: false, error: timedOut ? 'timeout' : 'offline', message: OFFLINE_MESSAGE, httpStatus: 0, network: true };
  }
  clearTimeout(timer);
  markOnline(true);

  let data = null;
  try { data = await res.json(); } catch { data = null; }

  if (res.ok) return { ...(data && typeof data === 'object' ? data : {}), ok: true, httpStatus: res.status };
  if (res.status === 401 && sentToken && onUnauthorized) {
    try { onUnauthorized(sentToken); } catch { /* the store's problem, not the caller's */ }
  }
  return {
    ok: false,
    error: data?.error || `http_${res.status}`,
    message: data?.message || 'Something went wrong. Try again in a minute.',
    httpStatus: res.status,
  };
}

export const api = {
  get: (path, opts) => request('GET', path, undefined, opts),
  post: (path, body = {}, opts) => request('POST', path, body, opts),
  put: (path, body = {}, opts) => request('PUT', path, body, opts),
  patch: (path, body = {}, opts) => request('PATCH', path, body, opts),
  // A DELETE carries a body only when one is given (leave a group and block).
  del: (path, opts, body) => request('DELETE', path, body, opts),
};

/** A failure worth retrying later (as opposed to "the server said no"). */
export function isTransient(r) {
  return !r.ok && (r.network || r.httpStatus === 0 || r.httpStatus === 429 || r.httpStatus >= 500);
}
